import React, { useEffect, useRef, useState } from 'react';
import './NotesTabsField.css';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

// Scan text for any "@<co-worker name> <message>*" the user has just completed
// with a trailing "*". Returns the matched mentions plus a copy of the text with
// each fired "*" removed — stripping the terminator makes firing idempotent, so
// the same segment never re-fires and loaded/legacy text never fires (this runs
// only on live typing). Mirrors the single-tab handler's one-pass-per-coworker
// extraction.
export const extractCompletedMentions = (text, coworkers) => {
  let cleaned = text;
  const mentions = [];
  for (const cw of coworkers) {
    if (!cw || !cw.name) continue;
    const pat = '@' + cw.name;
    const idx = cleaned.indexOf(pat);
    if (idx === -1) continue;
    const after = cleaned.substring(idx + pat.length);
    const starIdx = after.indexOf('*');
    if (starIdx === -1) continue;
    const message = after.substring(0, starIdx).trim();
    const starAbs = idx + pat.length + starIdx;
    cleaned = cleaned.substring(0, starAbs) + cleaned.substring(starAbs + 1);
    if (message) mentions.push({ cw, message });
  }
  return { mentions, cleaned };
};

// ── Tabbed notes field ──────────────────────────────────────────────
// Stored persistently as a JSON array of { title, content } in the
// underlying text column (e.g. Leads.description) or via a key-value
// endpoint. Legacy plain-text values degrade gracefully into a single
// default-titled tab.
export const parseNotesTabs = (raw, defaultTitle = 'Description') => {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr) && arr.length > 0 &&
            arr.every(t => t && typeof t === 'object' && 'content' in t)) {
          return arr.map((t, i) => ({
            title: (t.title && String(t.title).trim()) || (i === 0 ? defaultTitle : `Tab ${i + 1}`),
            content: t.content == null ? '' : String(t.content),
          }));
        }
      } catch (e) { /* not JSON — fall through to legacy handling */ }
    }
    // Legacy plain text (or anything non-JSON) becomes the first tab.
    return [{ title: defaultTitle, content: raw }];
  }
  return [{ title: defaultTitle, content: '' }];
};

export const serializeNotesTabs = (tabs) => JSON.stringify(
  tabs.map(t => ({ title: t.title, content: t.content }))
);

// Flatten tabbed notes into readable plain text (used e.g. when a Lead
// converts to a Project).
export const notesTabsToPlainText = (raw, defaultTitle = 'Description') => {
  const tabs = parseNotesTabs(raw, defaultTitle);
  const nonEmpty = tabs.filter(t => (t.content || '').trim() !== '');
  if (nonEmpty.length === 0) return '';
  if (nonEmpty.length === 1 && tabs.length === 1) return nonEmpty[0].content;
  return nonEmpty.map(t => `${t.title}:\n${t.content}`).join('\n\n');
};

const NotesTabsField = ({ value, defaultTitle = 'Description', onChange, rows = 6, mentionContext = {} }) => {
  const tabs = parseNotesTabs(value, defaultTitle);
  const [active, setActive] = useState(0);
  const [renaming, setRenaming] = useState(null); // index being renamed, or null
  const [renameDraft, setRenameDraft] = useState(''); // local text while renaming

  // @mention support
  const [coworkers, setCoworkers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState(0); // index of '@' in active content
  const textareaRef = useRef(null);

  const activeIdx = Math.min(active, tabs.length - 1);

  // Fetch co-workers once for the @mention dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/coworkers.php`);
        const data = await res.json();
        if (!cancelled && data.success) setCoworkers(data.data || []);
      } catch (e) { /* mentions simply unavailable */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const commit = (nextTabs, nextActive) => {
    onChange(serializeNotesTabs(nextTabs));
    if (nextActive !== undefined) setActive(nextActive);
  };

  // Fire a co-worker mention: create a Task + chat message (same endpoint the
  // legacy single-tab note field used).
  const fireMention = async (cw, message) => {
    let senderId = '';
    try { senderId = JSON.parse(sessionStorage.getItem('user') || '{}').id || ''; } catch (e) { /* none */ }
    try {
      await authFetch(`${CHAT_API_BASE}/coworkers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coworker_user_id: cw.user_id,
          coworker_name: cw.name,
          message,
          project_id: mentionContext.projectId || null,
          project_name: mentionContext.projectName || '',
          room_id: mentionContext.roomId || null,
          room_name: mentionContext.roomName || 'Room',
          sender_user_id: senderId,
        }),
      });
    } catch (e) { /* best-effort; note content still saved */ }
  };

  const updateContent = (text) => {
    let finalText = text;
    if (coworkers.length) {
      const { mentions, cleaned } = extractCompletedMentions(text, coworkers);
      if (mentions.length) {
        finalText = cleaned;
        mentions.forEach(m => fireMention(m.cw, m.message));
      }
    }
    commit(tabs.map((t, i) => i === activeIdx ? { ...t, content: finalText } : t));
  };

  // Detect an in-progress "@partial" before the caret and show the dropdown.
  const detectMention = (val, cursor) => {
    const textBefore = val.substring(0, cursor);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx !== -1 && !textBefore.substring(atIdx).includes('*') &&
        (atIdx === 0 || textBefore[atIdx - 1] === ' ' || textBefore[atIdx - 1] === '\n')) {
      setMentionSearch(textBefore.substring(atIdx + 1).toLowerCase());
      setMentionAnchor(atIdx);
      setShowDropdown(true);
      return;
    }
    setShowDropdown(false);
  };

  const onContentChange = (e) => {
    const text = e.target.value;
    detectMention(text, e.target.selectionStart);
    updateContent(text);
  };

  const filteredCoworkers = coworkers.filter(cw =>
    cw.name?.toLowerCase().includes(mentionSearch) ||
    cw.user_name?.toLowerCase().includes(mentionSearch)
  );

  // Replace the "@partial" being typed with the picked co-worker's name.
  const handleSelectCoworker = (cw) => {
    const content = tabs[activeIdx]?.content || '';
    const caret = textareaRef.current ? textareaRef.current.selectionStart : content.length;
    const before = content.substring(0, mentionAnchor);
    const after = content.substring(caret);
    const inserted = `@${cw.name} `;
    setShowDropdown(false);
    updateContent(before + inserted + after); // no '*', so nothing fires yet
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + inserted.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const renameTab = (idx, title) => {
    commit(tabs.map((t, i) => i === idx ? { ...t, title } : t));
  };

  // Rename uses a local draft so the field can be cleared and retyped freely —
  // committing every keystroke would round-trip through parseNotesTabs, which
  // re-defaults an empty title back to "Tab N" and snaps the text back.
  const beginRename = (idx, seedTitle) => {
    setRenameDraft(seedTitle);
    setRenaming(idx);
  };
  const commitRename = () => {
    if (renaming !== null) {
      renameTab(renaming, renameDraft.trim());
      setRenaming(null);
    }
  };

  const addTab = () => {
    const title = `Tab ${tabs.length + 1}`;
    const next = [...tabs, { title, content: '' }];
    commit(next, next.length - 1);
    beginRename(next.length - 1, title);
  };

  const removeTab = (idx) => {
    if (tabs.length <= 1) return;
    const next = tabs.filter((_, i) => i !== idx);
    commit(next, Math.max(0, idx - 1));
  };

  return (
    <div className="notes-tabs">
      <div className="notes-tabs-bar" role="tablist">
        {tabs.map((t, i) => (
          <div
            key={i}
            className={`notes-tab ${i === activeIdx ? 'active' : ''}`}
            onClick={() => setActive(i)}
            onDoubleClick={() => beginRename(i, t.title)}
            title="Double-click to rename"
          >
            {renaming === i ? (
              <input
                className="notes-tab-rename"
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                  else if (e.key === 'Escape') { e.preventDefault(); setRenaming(null); }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="notes-tab-title">{t.title}</span>
            )}
            {tabs.length > 1 && renaming !== i && (
              <button
                type="button"
                className="notes-tab-close"
                title="Remove tab"
                onClick={(e) => { e.stopPropagation(); removeTab(i); }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button type="button" className="notes-tab-add" title="Add tab" onClick={addTab}>+</button>
      </div>
      <div className="notes-tab-content-wrap">
        <textarea
          ref={textareaRef}
          className="form-textarea notes-tab-content"
          value={tabs[activeIdx]?.content || ''}
          onChange={onContentChange}
          onKeyDown={(e) => { if (showDropdown && e.key === 'Escape') setShowDropdown(false); }}
          placeholder={tabs[activeIdx]?.title || defaultTitle}
          rows={rows}
        />
        {showDropdown && filteredCoworkers.length > 0 && (
          <div className="notes-mention-dropdown">
            {filteredCoworkers.map(cw => (
              <button
                key={cw.contact_id || cw.user_id || cw.name}
                type="button"
                className="notes-mention-option"
                onMouseDown={(e) => { e.preventDefault(); handleSelectCoworker(cw); }}
              >
                <span className="notes-mention-name">{cw.name}</span>
                {cw.user_name && <span className="notes-mention-username">@{cw.user_name}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="notes-mention-hint">Use @name to mention a team member, end with *</div>
    </div>
  );
};

export default NotesTabsField;
